import 'core-js/stable';
import 'url-search-params-polyfill';

import { AuthProvider } from '@creditornot/wolt-auth/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserHistory } from 'history';
import React from 'react';
import { createRoot } from 'react-dom/client';

import adBookingsAgent from 'apis/ad-bookings/api';
import deliverySettingsApiAgent from 'apis/delivery-settings-api/api';
import financialDataRegistryAgent from 'apis/financial-data-registry/agent';
import inventoryManagementServiceAgent from 'apis/inventory-management-service/api';
import invoicingServiceAgent from 'apis/invoicing-service/api';
import legitimizerAgent from 'apis/legitimizer/api';
import merchantAdminServerAgent from 'apis/merchant-admin-server/api';
import merchantGatewayAgent from 'apis/merchant-gateway/api';
import merchantOnboardingServiceAgent from 'apis/merchant-onboarding-service/api';
import merchantPayoutServiceAgent from 'apis/merchant-payout-service/api';
import mipsAgent from 'apis/mips/api';
import pickerApiAgent from 'apis/picker-api/api';
import productInformationCatalogAgent from 'apis/product-information-catalog/api';
import productInformationServiceAgent from 'apis/product-information-service/api';
import restaurantApiAgent from 'apis/restaurant-api/api';
import restockingRulesAgent from 'apis/restocking-rules/api';
import vatCategoriesAgent from 'apis/vat-categories/api';
import wolfAgent from 'apis/wolf/api';
import woltAuthenticationAgent from 'apis/wolt-authentication/api';
import woltCapitalAPI from 'apis/wolt-capital/api';
import sendErrorReport from 'common/utils/sendErrorReport';
import initDatadogRum from 'core/initDatadogRum';
import avoInit from 'core/telemetry/avo/AvoInit';
import { authenticator, registerAuthInterceptor } from 'modules/wolt-auth';

import { isBrowserSupported, OutdatedBrowserNotification } from './browserSupport';
import AppLoadError from './core/AppLoadError';
import { init as initFacebook } from './core/facebook';
import { init as initGoogleAnalytics } from './core/googleAnalytics';
import { init as initGoogleClientApi } from './core/googleClient';
import { buildQueryClient } from './core/queryClient/buildQueryClient';
import { attachServiceWorker } from './serviceWorker';

console.info(`revision: ${process.env.APP_REVISION}`);
console.info(`environment: ${process.env.APP_ENV}`);
attachServiceWorker();

const container = document.getElementById('app');
const root = createRoot(container!);

if (isBrowserSupported()) {
  initDatadogRum();

  try {
    initFacebook();
    const history = createBrowserHistory();
    const queryClient = buildQueryClient();
    initGoogleAnalytics(history);
    initGoogleClientApi();
    avoInit();

    registerAuthInterceptor(
      adBookingsAgent,
      deliverySettingsApiAgent,
      financialDataRegistryAgent,
      inventoryManagementServiceAgent,
      invoicingServiceAgent,
      legitimizerAgent,
      merchantAdminServerAgent,
      merchantGatewayAgent,
      merchantOnboardingServiceAgent,
      merchantPayoutServiceAgent,
      mipsAgent,
      pickerApiAgent,
      productInformationCatalogAgent,
      productInformationServiceAgent,
      restaurantApiAgent,
      restockingRulesAgent,
      vatCategoriesAgent,
      wolfAgent,
      woltAuthenticationAgent,
      woltCapitalAPI,
    );

    const render = () => {
      const Root = require('./core/Root').default; // eslint-disable-line @typescript-eslint/no-var-requires
      root.render(
        <AuthProvider authenticator={authenticator}>
          <QueryClientProvider client={queryClient}>
            <Root />
          </QueryClientProvider>
        </AuthProvider>,
      );
    };
    render();

    if (module.hot) {
      module.hot.accept('./core/Root', render);
    }
  } catch (error) {
    console.error(error);
    sendErrorReport(error, 'index');
    root.render(<AppLoadError />);
  }
} else {
  root.render(<OutdatedBrowserNotification />);
}
